/**
 * openMatcher, tiny helper for dispatching the global "open matcher"
 * custom event consumed by the Header-mounted MiniMatcher.
 *
 * Use this anywhere we want a find-a-builder / find-a-trade / etc.
 * entry point to start from a Mini Matcher rather than navigating
 * directly to a full-screen workspace tool.
 *
 * Example:
 *   openMatcher({
 *     category: 'professional:builder',
 *     heading: 'Find your new home builder',
 *     sourcePlacement: 'workspace-find-a-builder-intercept',
 *   });
 *
 * The dispatched detail mirrors the props the Mini Matcher accepts -
 * `categoryOptions` short-circuits the picker step, `heading` and
 * `subheading` override the default copy, and `sourcePlacement` keeps
 * analytics + lead provenance clean.
 */
export function openMatcher(detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('buildpilot:open-matcher', { detail }),
  );
}

// 2026-06-05: "Home improvement builder" intercept now shows the
// full 12-build-type catalogue from `buildingTypes` (mock.js) rather
// than a hand-picked subset. Each option carries its AVIF icon so the
// pick-category step renders the same pill-with-icon language the
// homepage JourneyChips use.
//
// Category mapping aligns to the taxonomy keys served by the
// /api/opportunities/recommend engine. When a sub-category isn't a
// distinct taxonomy entry yet (e.g. landscaping → tradie:landscaper)
// we map to the closest, most likely-to-have-coverage business type.
const ALL_BUILD_TYPES_AS_OPTIONS = [
  { label: 'New homes',             category: 'professional:builder',             iconSrc: '/images/icons/new-homes.avif' },
  { label: 'Renovations',           category: 'professional:renovation_builder',  iconSrc: '/images/icons/renovations.avif' },
  { label: 'Choosing a block of land', category: 'professional:builder',          iconSrc: '/images/icons/choosing-land.avif' },
  { label: 'Changing builders',     category: 'professional:builder',             iconSrc: '/images/icons/changing-builders.avif' },
  { label: 'Invest or develop',     category: 'professional:builder',             iconSrc: '/images/icons/property-development.avif' },
  { label: 'Verandahs or pergolas', category: 'professional:home_improvement_builder', iconSrc: '/images/icons/verandahs-pergolas.avif' },
  { label: 'Landscaping',           category: 'tradie:landscaper',                iconSrc: '/images/icons/landscaping.avif' },
  { label: 'Sheds or garages',      category: 'professional:home_improvement_builder', iconSrc: '/images/icons/sheds-garages.avif' },
  { label: 'Decking',               category: 'professional:home_improvement_builder', iconSrc: '/images/icons/decking.avif' },
  // 2026-02-20: Interior design + Home design chips removed from the
  // builder picker. Those are professionals and are shortlisted via the
  // standalone Find a Professional tool.
  { label: 'Swimming pools',        category: 'professional:home_improvement_builder', iconSrc: '/images/icons/swimming-pools.avif' },
];

export const HOME_IMPROVEMENT_CATEGORY_OPTIONS = ALL_BUILD_TYPES_AS_OPTIONS;

export const FIND_BUILDER_PRESET = {
  new_home: {
    category: 'professional:builder',
    heading: 'Find your new home builder',
    sourcePlacement: 'find-builder-intercept-new-home',
  },
  renovation: {
    category: 'professional:renovation_builder',
    heading: 'Find your renovation builder',
    sourcePlacement: 'find-builder-intercept-renovation',
  },
  home_improvement: {
    categoryOptions: ALL_BUILD_TYPES_AS_OPTIONS,
    heading: 'What are you building?',
    subheading: 'Pick what you\'re building. We hand you 3 Adelaide builders worth talking to — nobody else.',
    sourcePlacement: 'find-builder-intercept-home-improvement',
  },
};
